// Per Ankh — Persistent Site Header
// Injects a sticky top bar (desktop) and a slim mobile bar whose hamburger
// opens the SideNav drawer. Auth-light: the CTA stays a single "Join" action.
// Also wires a lightweight scroll-reveal helper for [data-reveal] elements.

import {
  TOP_BAR_CTA, navFor, topBarLinksFrom, accountMenuLinksFrom,
} from './nav-links.js';
import { getClerkSession, onAuthChange } from '../../auth/clerk-session.js';
import { canOpenLabs, canEnterRealm } from '../../auth/member-roles.js';
import {
  attachNavPill, attachScrollBehaviour, attachDrawerMotion, openPanel, closePanel,
} from './header-motion.js';

(function () {
  // Every dropdown currently rendered (About, Account), so the ONE pair of document
  // listeners below can act on whatever was rendered last. Rebuilt on each
  // renderNav, which runs again on every auth change.
  const menus = [];
  const anyMenuOpen = () => menus.some((m) => !m.panel.hidden);
  const closeAll = (except = null) => menus.forEach((m) => { if (m !== except) m.close(); });

  // Close menus on an outside click, and on Escape. Registered once for the
  // lifetime of the page — a listener per render would stack on every auth change.
  document.addEventListener('click', (e) => {
    for (const m of menus) if (!m.root.contains(e.target)) m.close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = menus.find((m) => !m.panel.hidden);
    if (open) {
      open.close();
      open.trigger.focus();
    }
  });

  /**
   * One dropdown: a trigger button and a panel of rich links (icon, label,
   * description). Opening one closes the others.
   *
   * @param {string} label
   * @param {Array<object>} links
   * @param {{align?: 'left'|'right', cls?: string}} [opts]
   */
  function buildDropdown(label, links, { align = 'right', cls = '' } = {}) {
    const root = document.createElement('div');
    root.className = 'site-header__menu' + (cls ? ' ' + cls : '');

    const trigger = document.createElement('button');
    trigger.className = 'site-header__menu-trigger';
    trigger.type = 'button';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'true');
    trigger.textContent = label;
    if (links.some((l) => isActive(l.href))) trigger.classList.add('is-active');
    root.appendChild(trigger);

    const panel = document.createElement('div');
    panel.className = 'site-header__menu-panel site-header__menu-panel--' + align;
    panel.hidden = true;
    panel.setAttribute('role', 'menu');
    for (const l of links) panel.appendChild(menuLink(l));
    root.appendChild(panel);

    const menu = {
      root, trigger, panel,
      open() {
        closeAll(menu);
        openPanel(panel);
        trigger.setAttribute('aria-expanded', 'true');
      },
      close() {
        if (panel.hidden) return;
        closePanel(panel);
        trigger.setAttribute('aria-expanded', 'false');
      },
    };
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (panel.hidden) menu.open(); else menu.close();
    });
    menus.push(menu);
    return root;
  }

  /** A rich dropdown row: icon, label, one-line description, optional badge. */
  function menuLink(l) {
    const a = document.createElement('a');
    a.className = 'site-header__menu-link';
    a.href = l.href;
    a.setAttribute('role', 'menuitem');
    if (isActive(l.href)) a.setAttribute('aria-current', 'page');

    const icon = document.createElement('span');
    icon.className = 'site-header__menu-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = l.icon || '';
    a.appendChild(icon);

    const text = document.createElement('span');
    text.className = 'site-header__menu-text';
    const label = document.createElement('span');
    label.className = 'site-header__menu-label';
    label.textContent = l.label;
    text.appendChild(label);
    if (l.description) {
      const desc = document.createElement('span');
      desc.className = 'site-header__menu-desc';
      desc.textContent = l.description;
      text.appendChild(desc);
    }
    a.appendChild(text);

    if (l.badge) {
      const badge = document.createElement('span');
      badge.className = 'site-header__menu-badge';
      badge.id = 'header-inbox-badge';
      badge.hidden = true;
      a.appendChild(badge);
    }
    return a;
  }

  function init() {
    if (!document.body || document.querySelector('.site-header')) return;

    // Signal the CSS to hide SideNav's floating button when present.
    document.documentElement.classList.add('has-site-header');

    const root = document.createElement('header');
    root.className = 'site-header';
    root.setAttribute('role', 'banner');

    const inner = document.createElement('div');
    inner.className = 'site-header__inner';

    // ── Brand ───────────────────────────────────────────────────────────────
    const brand = document.createElement('a');
    brand.className = 'site-header__brand';
    brand.href = '/';

    const logo = document.createElement('img');
    logo.className = 'site-header__logo';
    logo.src = '/assets/images/logo/PerAnkhLogo.png';
    logo.alt = 'Per Ankh Entheogenic Church Logo';
    logo.width = 42;
    logo.height = 42;

    const wordmark = document.createElement('span');
    wordmark.className = 'site-header__wordmark';
    wordmark.innerHTML =
      '<span class="site-header__wordmark-title">Per Ankh</span>' +
      '<span class="site-header__wordmark-sub">Entheogenic Church</span>';

    brand.appendChild(logo);
    brand.appendChild(wordmark);
    inner.appendChild(brand);

    // ── Desktop nav ─────────────────────────────────────────────────────────
    // Built from the ONE shared list (navFor), so the top bar can never again
    // offer a different set of links from the drawer. Public links sit flat; the
    // member's own area goes behind a "Members" menu so the bar stays readable.
    const nav = document.createElement('nav');
    nav.className = 'site-header__nav';
    nav.setAttribute('aria-label', 'Primary');
    inner.appendChild(nav);

    // Holds the links that depend on who is signed in, so a sign-in redraws just
    // this part rather than the whole bar.
    const memberMenu = document.createElement('div');
    memberMenu.className = 'site-header__menu';
    memberMenu.hidden = true;
    inner.appendChild(memberMenu);

    renderNav(nav, memberMenu, null);

    // ── Sign In (guest) + CTA ───────────────────────────────────────────────
    const signIn = document.createElement('a');
    signIn.className = 'site-header__signin';
    signIn.href = '/member/login';
    signIn.textContent = 'Sign In';
    inner.appendChild(signIn);

    const cta = document.createElement('a');
    cta.className = 'site-header__cta';
    cta.href = TOP_BAR_CTA.href;
    cta.textContent = TOP_BAR_CTA.label;
    inner.appendChild(cta);

    // ── Mobile hamburger (opens the SideNav drawer) ─────────────────────────
    const burger = document.createElement('button');
    burger.className = 'site-header__burger';
    burger.type = 'button';
    burger.setAttribute('aria-label', 'Open navigation menu');

    const lines = document.createElement('span');
    lines.className = 'site-header__burger-lines';
    lines.innerHTML = '<span></span><span></span><span></span>';
    burger.appendChild(lines);
    burger.appendChild(document.createTextNode('Menu'));

    burger.addEventListener('click', (e) => {
      e.preventDefault();
      if (window.perAnkhSideNav && typeof window.perAnkhSideNav.open === 'function') {
        window.perAnkhSideNav.open();
      } else {
        // Fallback if SideNav hasn't mounted yet.
        window.location.href = '/';
      }
    });
    inner.appendChild(burger);

    root.appendChild(inner);
    document.body.prepend(root);

    initReveal();
    applyAuthState(signIn, cta);

    attachNavPill(nav);
    attachScrollBehaviour(root, anyMenuOpen);
    attachDrawerMotion();
  }

  // — Build the desktop navigation from the shared list —
  // Called at boot for a guest and again on every auth change. Redeclaring the
  // whole bar each time keeps one source of truth: there is no "add the member
  // links" branch that can fall out of step with the list.
  function renderNav(nav, memberMenu, user) {
    const links = navFor({ user, canOpenLabs: canOpenLabs(user), canEnterRealm: canEnterRealm(user) });
    menus.length = 0;

    // Flat links in list order, with each `menu` group collapsed into ONE dropdown
    // at the place its first member would have stood.
    nav.querySelectorAll(':scope > :not(.site-header__pill)').forEach((n) => n.remove());
    const bar = topBarLinksFrom(links);
    const placed = new Set();
    for (const l of bar) {
      if (l.menu) {
        if (placed.has(l.menu)) continue;
        placed.add(l.menu);
        const group = bar.filter((x) => x.menu === l.menu);
        nav.appendChild(buildDropdown(l.menu, group, { align: 'left', cls: 'site-header__menu--inline' }));
        continue;
      }
      const a = document.createElement('a');
      a.className = 'site-header__link' + (isActive(l.href) ? ' is-active' : '');
      a.href = l.href;
      a.textContent = l.topBarLabel || l.label;
      if (isActive(l.href)) a.setAttribute('aria-current', 'page');
      nav.appendChild(a);
    }

    const menuLinks = accountMenuLinksFrom(links);
    memberMenu.innerHTML = '';
    memberMenu.hidden = menuLinks.length === 0;
    if (menuLinks.length === 0) return;
    memberMenu.appendChild(buildDropdown('Account', menuLinks, { align: 'right' }));
  }

  async function applyAuthState(signIn, cta) {
    const session = await getClerkSession();
    updateForAuth(session?.user || null, signIn, cta);
    onAuthChange(session, (user) => updateForAuth(user, signIn, cta));
  }

  function updateForAuth(user, signIn, cta) {
    const role = user?.publicMetadata?.role;

    // The whole bar is rebuilt from the shared list, so the member links appear and
    // disappear with the session and can never drift from the drawer's.
    const nav = document.querySelector('.site-header__nav');
    const memberMenu = document.querySelector('.site-header__menu');
    if (nav && memberMenu) renderNav(nav, memberMenu, user);

    if (user) {
      signIn.style.display = 'none';
      if (role === 'admin') {
        cta.textContent = 'Admin';
        cta.href = '/admin/dashboard';
      } else {
        cta.textContent = 'Dashboard';
        cta.href = '/member/dashboard';
      }
    } else {
      signIn.style.display = '';
      cta.textContent = 'Join';
      cta.href = '/become-a-member';
    }
  }

  function isActive(href) {
    try {
      const p = window.location.pathname;
      if (href === '/') return p === '/' || p === '/pages/index.html';
      return p === href || p.startsWith(href + '/');
    } catch {
      return false;
    }
  }

  // — Scroll reveal (respects reduced motion) —
  function initReveal() {
    const reduce = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    els.forEach((el) => obs.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
